import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, UrgentTag, STATUS_META } from "@/components/StatusBadge";
import { toast } from "sonner";

const COLUMNS = ["SUBMITTED", "REVIEWING", "APPROVED", "IN PROGRESS", "QUALITY CHECK", "READY FOR COLLECTION", "COMPLETED"];
// map extra statuses into board columns
const MAP = { QUOTED: "REVIEWING", "AWAITING EXTERNAL TRADE": "IN PROGRESS" };

export default function Board() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [q, setQ] = useState("");
  const [fMember, setFMember] = useState("ALL");
  const [fService, setFService] = useState("ALL");
  const [fPriority, setFPriority] = useState("ALL");

  const load = () => api.get("/jobs").then((r) => setJobs(r.data.filter((j) => j.status !== "CANCELLED"))).catch(() => {});
  useEffect(() => { load(); }, []);

  const colOf = (s) => MAP[s] || s;
  const move = async (jobId, status) => {
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, status } : j)));
    try { await api.patch(`/jobs/${jobId}/status`, { status }); toast.success(`Moved to ${STATUS_META[status].label}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  const members = [...new Set(jobs.map((j) => j.member_name))].sort();
  const services = [...new Set(jobs.flatMap((j) => j.services || []))].sort();
  const match = (j) =>
    (fMember === "ALL" || j.member_name === fMember) &&
    (fService === "ALL" || (j.services || []).includes(fService)) &&
    (fPriority === "ALL" || j.priority === fPriority) &&
    (!q || `${j.job_number} ${j.title} ${j.member_name}`.toLowerCase().includes(q.toLowerCase()));
  const visible = jobs.filter(match);
  const selCls = "panel rounded-sm px-3 py-2 text-xs text-[#C2BEB5] outline-none focus:border-[#C5A059] min-w-0";

  return (
    <div>
      <PageHeader eyebrow="Operations" title="Trade Board" />

      <div className="flex flex-wrap gap-2 mb-5">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="board-search" placeholder="Search job no. / title / member"
          className="panel rounded-sm px-3 py-2 text-sm outline-none focus:border-[#C5A059] flex-1 min-w-[160px]" />
        <select value={fMember} onChange={(e) => setFMember(e.target.value)} className={selCls} data-testid="board-filter-member">
          <option value="ALL">All members</option>{members.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={fService} onChange={(e) => setFService(e.target.value)} className={selCls} data-testid="board-filter-service">
          <option value="ALL">All services</option>{services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={fPriority} onChange={(e) => setFPriority(e.target.value)} className={selCls} data-testid="board-filter-priority">
          <option value="ALL">Any priority</option><option value="URGENT">Urgent</option><option value="STANDARD">Standard</option>
        </select>
      </div>

      {/* Desktop / tablet kanban */}
      <div className="hidden lg:flex gap-3 overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const items = visible.filter((j) => colOf(j.status) === col);
          const meta = STATUS_META[col];
          return (
            <div key={col} data-testid={`col-${col}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) { move(dragId, col); setDragId(null); } }}
              className="w-[280px] shrink-0">
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                  <span className="text-xs uppercase tracking-wider text-[#C2BEB5]">{meta.label}</span>
                </div>
                <span className="font-mono text-xs text-[#8C867A]">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[120px] rounded-sm p-1" style={{ background: "#0f0f11" }}>
                {items.map((j) => (
                  <div key={j.id} draggable onDragStart={() => setDragId(j.id)}
                    onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`card-${j.id}`}
                    className="panel rounded-sm p-3 cursor-pointer hover:border-[#C5A059] transition-colors active:opacity-60">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="font-mono text-[0.65rem] brass-text">{j.job_number}</span>
                      {j.priority === "URGENT" && <UrgentTag />}
                    </div>
                    <div className="text-sm text-[#E2DFD7] leading-snug mb-2">{j.title}</div>
                    <div className="text-[0.65rem] text-[#8C867A] mb-2">{(j.services || []).slice(0, 3).join(" · ")}</div>
                    <div className="flex items-center justify-between text-[0.65rem]">
                      <span className="text-[#C2BEB5] truncate">{j.member_name}</span>
                      <span className="font-mono text-[#8C867A]">{fmtDate(j.target_date)}</span>
                    </div>
                    <select value={colOf(j.status)} onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); move(j.id, e.target.value); }} data-testid={`card-status-${j.id}`}
                      className="mt-2 w-full bg-[#1A1A1E] border hairline rounded-sm px-2 py-1 text-[0.65rem] text-[#C2BEB5] outline-none">
                      {COLUMNS.map((c) => <option key={c} value={c}>{STATUS_META[c].label}</option>)}
                    </select>
                  </div>
                ))}
                {items.length === 0 && <div className="text-[0.65rem] text-[#5c5850] text-center py-6">—</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile vertical list */}
      <div className="lg:hidden space-y-2">
        {visible.length === 0 && <div className="text-sm text-[#8C867A] py-10 text-center">No jobs match these filters.</div>}
        {visible.map((j) => (
          <div key={j.id} data-testid={`mcard-${j.id}`} className="panel rounded-sm p-3.5">
            <button onClick={() => navigate(`/jobs/${j.id}`)} className="w-full text-left">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-mono text-xs brass-text">{j.job_number}</span>
                {j.priority === "URGENT" && <UrgentTag />}
              </div>
              <div className="text-sm text-[#E2DFD7] mb-1">{j.title}</div>
              <div className="text-[0.65rem] text-[#8C867A] mb-2.5">{j.member_name} · {(j.services || []).slice(0, 2).join(" · ")} · {fmtDate(j.target_date)}</div>
            </button>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={j.status} />
              <select value={colOf(j.status)} onChange={(e) => move(j.id, e.target.value)} data-testid={`mcard-status-${j.id}`}
                className="ml-auto panel rounded-sm px-2 py-1.5 text-xs text-[#C2BEB5] outline-none">
                {COLUMNS.map((c) => <option key={c} value={c}>{STATUS_META[c].label}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
